import { alpha, Box, Typography } from '@mui/material';
import * as React from 'react';
import { Roi } from '~/domain/dicom/dicomstructureset';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import ExportListItem from './ExportListItem';
import DoseExportDetails from './ExportListItem/DoseExportDetails';
import ImageExportDetails from './ExportListItem/ImageExportDetails';
import StructureSetExportDetails from './ExportListItem/StructureSetExportDetails';

type ExportableItemsListProps = {
    exportableImages: Image3D[];
    exportableStructureSets: StructureSet[];
    exportableDoses: DoseMatrix[];
    selectedImages: Image3D[];
    selectedStructureSets: StructureSet[];
    selectedDoses: DoseMatrix[];
    structureSelections: Record<string, Roi[]>;
    defaultExpanded: string;
    handleSelectImage: (image: Image3D) => void;
    handleSelectStructureSet: (structureSet: StructureSet) => void;
    handleSelectStructure: (structureSetId: string, structure: Roi) => void;
    handleSelectDose: (dose: DoseMatrix) => void;
};

const ExportDatasetModal = ({
    exportableImages,
    exportableStructureSets,
    exportableDoses,
    selectedImages,
    selectedStructureSets,
    selectedDoses,
    structureSelections,
    defaultExpanded,
    handleSelectImage,
    handleSelectStructureSet,
    handleSelectStructure,
    handleSelectDose,
}: ExportableItemsListProps) => {
    const [expanded, setExpanded] = React.useState<string>(defaultExpanded);

    const handleExpand =
        (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : '');
        };

    const hasExportableItems =
        exportableStructureSets?.length ||
        exportableDoses?.length ||
        exportableImages?.length;

    return (
        <Box
            sx={(theme) => ({
                borderBottom: `1px solid ${theme.palette.divider}`,
            })}
        >
            {!hasExportableItems && (
                <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    p={2}
                    sx={(theme) => ({
                        borderRadius: `${theme.shape.borderRadius}px`,
                        border: `1px solid ${theme.palette.info.main}`,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                    })}
                >
                    <Typography variant="body2" color="primary">
                        There is no generated datasets to export.
                    </Typography>
                </Box>
            )}

            {exportableImages?.map((image) => (
                <ExportListItem
                    key={image.dicomUid}
                    label={image.seriesMetadata.seriesDescription}
                    modality={image.seriesMetadata.modality}
                    expanded={expanded === image.dicomUid}
                    onChange={handleExpand(image.dicomUid)}
                    isSelected={selectedImages.includes(image)}
                    onSelect={() => handleSelectImage(image)}
                    isExported={image.isExported}
                    details={<ImageExportDetails image={image} />}
                />
            ))}

            {exportableStructureSets?.map((structureSet) => (
                <ExportListItem
                    key={structureSet.dicomUid}
                    label={structureSet.structureSetLabel}
                    modality={Modality.RTSTRUCT}
                    expanded={expanded === structureSet.dicomUid}
                    onChange={handleExpand(structureSet.dicomUid)}
                    isSelected={selectedStructureSets.includes(structureSet)}
                    onSelect={() => handleSelectStructureSet(structureSet)}
                    isExported={structureSet.isExported}
                    details={
                        <StructureSetExportDetails
                            structureSet={structureSet}
                            selectedStructures={
                                structureSelections[structureSet.dicomUid] || []
                            }
                            onSelectStructure={(structure) =>
                                handleSelectStructure(
                                    structureSet.dicomUid,
                                    structure
                                )
                            }
                        />
                    }
                />
            ))}

            {exportableDoses?.map((dose) => (
                <ExportListItem
                    key={dose.dicomUid}
                    label={dose.plan?.label ?? 'Dose'}
                    modality={Modality.RTDOSE}
                    expanded={expanded === dose.dicomUid}
                    onChange={handleExpand(dose.dicomUid)}
                    isSelected={selectedDoses.includes(dose)}
                    onSelect={() => handleSelectDose(dose)}
                    isExported={dose.isExported}
                    details={<DoseExportDetails dose={dose} />}
                />
            ))}
        </Box>
    );
};

export default ExportDatasetModal;
